import React from 'react';
import PropTypes from 'prop-types';

import {Box, Chip, Stack, Tooltip, Typography} from 'views/components';
import {Translate, translate} from 'views/module';

const GRADE_STYLES = {
  PASS: {
    color: '#008A00',
    backgroundColor: '#E3FAE5',
    borderColor: '#BCE9C0',
  },
  FAIL: {
    color: '#C4512C',
    backgroundColor: '#FFF1EC',
    borderColor: '#F4C7B5',
  },
  UNCERTAIN: {
    color: '#B77900',
    backgroundColor: '#FFF7E5',
    borderColor: '#EFDCAE',
  },
  SKIPPED_INTERVENTION: {
    color: '#526071',
    backgroundColor: '#F7F8FA',
    borderColor: '#E4E5EA',
  },
};

const formatCreatedAt = (createdAt) => {
  const createdAtDate = new Date(createdAt);

  if (Number.isNaN(createdAtDate.getTime())) {
    return '';
  }

  return createdAtDate.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

// Each record carries its own rubric snapshot, so the category label reflects
// the rubric the grade was scored against, not the current one.
const getFailureCategoryLabel = (mark) => {
  const category = (mark.rubricSnapshot?.failureCategories || [])
      .find((failureCategory) => failureCategory.key === mark.failureCategory);

  return category?.label || mark.failureCategory;
};

const getConfidenceLabel = (confidence) => {
  if (typeof confidence !== 'number') {
    return '';
  }

  const confidencePercent = confidence <= 1 ? Math.round(confidence * 100) : Math.round(confidence);

  return translate('case.Detail.Marks.Confidence', {confidence: confidencePercent});
};

const CaseMarkPanel = (props) => {
  const {
    displayMarkKey,
    marks,
    loaded,
    loadFailed,
  } = props;

  const sortedMarks = React.useMemo(() => {
    return [...marks].sort((firstMark, secondMark) => {
      return `${secondMark.createdAt}`.localeCompare(`${firstMark.createdAt}`);
    });
  }, [marks]);

  return (
    <Box
      sx={{
        backgroundColor: '#FFFFFF',
        border: '1px solid #ECECF1',
        borderRadius: '10px',
        overflow: 'hidden',
      }}
    >
      {
        !loaded && (
          <Typography
            sx={{
              px: 3,
              py: 2,
              fontSize: '12px',
              color: '#9A9BAE',
            }}
          >
            ...
          </Typography>
        )
      }
      {
        loaded && loadFailed && (
          <Typography
            sx={{
              px: 3,
              py: 2,
              fontSize: '12px',
              color: '#D85D31',
            }}
          >
            <Translate expression='case.Detail.Marks.LoadFailed' />
          </Typography>
        )
      }
      {
        loaded && !loadFailed && sortedMarks.length === 0 && (
          <Typography
            sx={{
              px: 3,
              py: 2,
              fontSize: '12px',
              color: '#9A9BAE',
            }}
          >
            <Translate expression='case.Detail.Marks.Empty' />
          </Typography>
        )
      }
      {
        loaded && !loadFailed &&
        sortedMarks.map((mark, index) => {
          const gradeStyle = GRADE_STYLES[mark.grade] || GRADE_STYLES.SKIPPED_INTERVENTION;
          const isDisplayGrade = mark.markKey === displayMarkKey;
          const confidenceLabel = getConfidenceLabel(mark.confidence);
          const markerLabel = mark.markerType === 'HUMAN' ?
            translate('case.Detail.Marks.Human') :
            translate('case.Detail.Marks.Teacher');

          return (
            <Stack
              key={mark.markKey}
              gap={1}
              sx={{
                px: 3,
                py: 2.5,
                borderBottom: index === sortedMarks.length - 1 ? 'none' : '1px solid #ECECF1',
              }}
            >
              <Stack direction='row' sx={{gap: 1, alignItems: 'center', flexWrap: 'wrap'}}>
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: 700,
                    color: '#050A14',
                  }}
                >
                  {markerLabel}
                </Typography>
                {
                  mark.markerType === 'HUMAN' && mark.reviewerRn &&
                  <Typography
                    sx={{
                      fontSize: '12px',
                      fontWeight: 500,
                      color: '#9A9BAE',
                      wordBreak: 'break-word',
                    }}
                  >
                    {translate('case.Detail.Marks.ReviewedBy', {reviewerRn: mark.reviewerRn})}
                  </Typography>
                }
                <Chip
                  label={translate(`case.Detail.Marks.Grades.${mark.grade}`)}
                  size='small'
                  sx={{
                    'height': '20px',
                    'fontSize': '11px',
                    'fontWeight': 600,
                    'color': gradeStyle.color,
                    'backgroundColor': gradeStyle.backgroundColor,
                    'border': `1px solid ${gradeStyle.borderColor}`,
                    'borderRadius': '5px',
                    '& .MuiChip-label': {
                      px: '7px',
                    },
                  }}
                />
                {
                  mark.autoFailConditionTriggered &&
                  <Chip
                    label={translate('case.Detail.Marks.AutoFail')}
                    size='small'
                    sx={{
                      'height': '20px',
                      'fontSize': '11px',
                      'fontWeight': 600,
                      'color': '#FFFFFF',
                      'backgroundColor': '#C4512C',
                      'borderRadius': '5px',
                      '& .MuiChip-label': {
                        px: '7px',
                      },
                    }}
                  />
                }
                {
                  isDisplayGrade &&
                  <Tooltip title={translate('case.Detail.Marks.DisplayGradeHint')}>
                    <Chip
                      label={translate('case.Detail.Marks.DisplayGrade')}
                      size='small'
                      sx={{
                        'height': '20px',
                        'fontSize': '11px',
                        'fontWeight': 600,
                        'color': '#526071',
                        'backgroundColor': '#F7F8FA',
                        'border': '1px solid #E4E5EA',
                        'borderRadius': '5px',
                        '& .MuiChip-label': {
                          px: '7px',
                        },
                      }}
                    />
                  </Tooltip>
                }
              </Stack>
              <Stack direction='row' sx={{gap: 1.5, alignItems: 'center', flexWrap: 'wrap'}}>
                {
                  mark.failureCategory &&
                  <Typography
                    sx={{
                      fontSize: '12px',
                      fontWeight: 700,
                      color: '#C4512C',
                    }}
                  >
                    {getFailureCategoryLabel(mark)}
                  </Typography>
                }
                {
                  confidenceLabel &&
                  <Typography
                    sx={{
                      fontSize: '12px',
                      fontWeight: 500,
                      color: '#9A9BAE',
                    }}
                  >
                    {confidenceLabel}
                  </Typography>
                }
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#9A9BAE',
                  }}
                >
                  {formatCreatedAt(mark.createdAt)}
                </Typography>
              </Stack>
              {
                mark.reasoning &&
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 400,
                    color: '#050A14',
                    lineHeight: '20px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {mark.reasoning}
                </Typography>
              }
            </Stack>
          );
        })
      }
    </Box>
  );
};

CaseMarkPanel.propTypes = {
  displayMarkKey: PropTypes.string,
  marks: PropTypes.array.isRequired,
  loaded: PropTypes.bool.isRequired,
  loadFailed: PropTypes.bool.isRequired,
};

export default React.memo(CaseMarkPanel);
